/* eslint-disable operator-linebreak */
import css from './styles/factoring-client-selector.scss';
import { Menu, MenuItem } from '@mui/material';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'shamrock-clover-ui/dist/clover/components/Spinner/Spinner';
import classNames from 'classnames';
import { Button, Dialog } from 'shamrock-clover-ui';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LocalAuth from '@shamrock-core/common/authentication/local-authentication';
import invoiceManagerService from '../../services/invoice-manager.service';
import {
  getActiveAccountInfo,
  getCadenceAccounts,
  getIMUser,
  getLoadingCadenceAccounts,
  removeCadenceAccount,
  setActiveAccountInfo,
  setIMUser,
} from '../../redux/actions/appActions';
import { useDispatch, useSelector } from 'react-redux';
import AddFactoringClientDialog from './add-factoring-client.dialog';
import { sortAlphabetically } from '../../utils/utils';
import { Portal } from 'react-portal';
import SmartTooltip from '../core/SmartTooltip';
import ViewAsCustomerSelector from './view-as-customer-selector';
import { RootState } from 'bootstrap';
import { Snackbar } from 'shamrock-clover-ui/dist/clover/components/Snackbar/Snackbar';
import { Icon } from 'shamrock-clover-ui/dist/clover/components/Icon/Icon';
import { useTranslation } from 'react-i18next';
import arrowDropdown from '../../assets/images/arrow-dropdown.svg';

interface Props {}

const FactoringClientSelector = ({}: Props) => {
  const permissions = useSelector((state: RootState) => state.app.permissions);
  const isRepAdmin = permissions?.includes('carrierpro.RepCompanyAdmin');
  const hasViewCustomerPermission = !!permissions?.includes('invoicemanager.view_customer');
  const [isLoading, setIsLoading] = React.useState(false);
  const [anchorElFactoring, setAnchorElFactoring] = React.useState(undefined);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [isRemoveAccountDialogOpen, setIsRemoveAccountDialogOpen] = React.useState(false);
  const [accountToBeRemoved, setAccountToBeRemoved] = React.useState(undefined);
  const [showSnackbar, setShowSnackbar] = React.useState(false);
  const imUser = useSelector(getIMUser);
  const activeAccountInfo = useSelector(getActiveAccountInfo);
  const cadenceAccounts = useSelector(getCadenceAccounts);
  const loadingAccounts = useSelector(getLoadingCadenceAccounts);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const cpua4691Web = features.includes('CPUA-4691-WEB');

  const changeAccount = async (clientNumber) => {
    setIsLoading(true);
    const updatedUser = {
      user_id: LocalAuth.getUser().email as string,
      active_account: clientNumber,
      accounts: imUser.accounts.map((i) => ({
        reference_id: i.reference_id,
      })),
    };
    await invoiceManagerService.saveIMUser(imUser.user_id, updatedUser, false);
    dispatch(setIMUser({ ...imUser, active_account: clientNumber }));
    dispatch(setActiveAccountInfo(cadenceAccounts.find((acct) => acct.client_number === clientNumber)));
    setAnchorElFactoring(null);
    setIsLoading(false);
  };

  const openAddAccountDialog = () => {
    setOpenDialog(true);
    setAnchorElFactoring(null);
  };
  const handleSubmitClicked = () => {
    setOpenDialog(false);
  };

  const handleCloseClicked = () => {
    setOpenDialog(false);
  };

  const removeAccountFromUser = () => {
    setOpenDialog(false);
    setIsRemoveAccountDialogOpen(false);
    dispatch(removeCadenceAccount(accountToBeRemoved));
    setAnchorElFactoring(null);
  };

  const removeAccountButton = (
    <span key="conf-rem-btn" style={{ float: 'right' }}>
      <Button onClick={removeAccountFromUser} data-testid="remove-account-confirmation-button">
        {t('submit')}
      </Button>
    </span>
  );

  const cancelRemoveAccountButton = (
    <span key="cancel-rem-btn" style={{ float: 'left' }}>
      <Button onClick={() => setIsRemoveAccountDialogOpen(false)} data-testid="remove-account-cancel-button">
        {t('cancel')}
      </Button>
    </span>
  );

  const openRemoveAccountDialog = (account) => {
    setAccountToBeRemoved(account);
    setIsRemoveAccountDialogOpen(true);
  };

  const displayName = useMemo(() => {
    if (cadenceAccounts.length <= 0 && !imUser?.last_active_account) {
      if (loadingAccounts) {
        return 'Loading...';
      }

      return 'No Accounts';
    }

    if (activeAccountInfo?.client_number) {
      let displayName = `${activeAccountInfo.client_number} - ${activeAccountInfo?.name}`;

      if (imUser?.last_active_account) {
        // This is for internal users only, using View As
        displayName = 'You are viewing: ' + displayName;
      } else if (activeAccountInfo?.mc_number) {
        displayName = displayName + ` (MC # ${activeAccountInfo.mc_number})`;
      }

      return displayName;
    } else if (!loadingAccounts) {
      return 'No Accounts';
    }

    return 'Loading...';
  }, [loadingAccounts, activeAccountInfo?.client_number, JSON.stringify(imUser), cadenceAccounts.length]);

  const numMenuItems = cadenceAccounts ? cadenceAccounts.length : 0;
  const [menuRefs, setMenuRefs] = useState([]);
  useEffect(() => {
    setMenuRefs((menuRefs) =>
      Array(numMenuItems)
        .fill(null)
        .map((_, i) => menuRefs[i] || createRef()),
    );
  }, [numMenuItems]);

  const externalUserMenu = (
    <div>
      {isLoading && (
        <div>
          <div className={css.loadingContainer} />
          <div className={css.loadingSpinner}>
            <Spinner size="50" />
          </div>
        </div>
      )}

      {cadenceAccounts &&
        [...cadenceAccounts]
          .sort((accountA, accountB) => sortAlphabetically(accountA.client_name, accountB.client_name))
          .map((account, i) => (
            <div className={css.item} key={account.client_number}>
              <div className={css.name}>
                <MenuItem style={{ width: 'auto' }} onClick={() => changeAccount(account.client_number)}>
                  <div className={css.menuItem}>
                    {account.client_number} - {account.client_name}
                  </div>
                </MenuItem>
              </div>
              <div className={css.icon} ref={menuRefs[i]} data-testid="remove-account-button">
                <RemoveCircleIcon onClick={() => openRemoveAccountDialog(account.client_number)} />
              </div>
              <SmartTooltip title={t('remove_account')} target={menuRefs[i]} />
            </div>
          ))}
      {!cpua4691Web && (
        <MenuItem style={{ width: 'auto' }} onClick={openAddAccountDialog}>
          <div className={css.menuItem} data-testid="add-account-button">
            {t('add_account')}
          </div>
        </MenuItem>
      )}
    </div>
  );

  return (
    <>
      <div
        className={classNames(css.menuItem, css.updatedFactoringAccount)}
        onClick={(event) => {
          if (cadenceAccounts?.length > 0 || !cpua4691Web || isRepAdmin) {
            setAnchorElFactoring(event.currentTarget);
          }
        }}
      >
        {imUser.last_active_account && (
          <div style={{ marginRight: '8px' }}>
            <Icon icon="eyeShow" size="17" />
          </div>
        )}
        {displayName}
        <div className={anchorElFactoring ? css.updatedOpen : css.updatedClosed} data-testid="accounts-dropdown-button">
          {<img src={arrowDropdown} />}
        </div>
      </div>
      {hasViewCustomerPermission ? (
        <Menu
          classes={{ paper: css.viewAsMenuRootNew }}
          anchorEl={anchorElFactoring}
          keepMounted
          container={document.body}
          open={Boolean(anchorElFactoring)}
          onClose={() => setAnchorElFactoring(null)}
          MenuListProps={{ disablePadding: true }}
        >
          {Boolean(anchorElFactoring) && (
            <ViewAsCustomerSelector
              changeAccount={changeAccount}
              onClose={() => setAnchorElFactoring(null)}
              onChangesSaved={() => {
                setShowSnackbar(true);
                setAnchorElFactoring(null);
              }}
            />
          )}
        </Menu>
      ) : (
        <Menu
          classes={{ paper: css.factoringMenuRoot }}
          anchorEl={anchorElFactoring}
          keepMounted
          open={Boolean(anchorElFactoring)}
          onClose={() => setAnchorElFactoring(null)}
        >
          {externalUserMenu}
        </Menu>
      )}
      {openDialog && (
        <Portal>
          <AddFactoringClientDialog
            open={openDialog}
            onCloseClicked={handleCloseClicked}
            onSubmitClicked={handleSubmitClicked}
          />
        </Portal>
      )}
      {isRemoveAccountDialogOpen && (
        <Portal>
          <Dialog
            title={t('remove_account')}
            open={isRemoveAccountDialogOpen}
            onClose={() => setIsRemoveAccountDialogOpen(false)}
            content={<div>{t('remove_account_confirmation', { account: accountToBeRemoved })}</div>}
            actions={[cancelRemoveAccountButton, removeAccountButton]}
          />
        </Portal>
      )}
      {showSnackbar && (
        <Portal>
          <Snackbar
            open={!!showSnackbar}
            onClose={() => setShowSnackbar(false)}
            duration={5000}
            message="Changes made successfully"
            variant="success"
            position="bottomCenter"
            width="230px"
          />
        </Portal>
      )}
    </>
  );
};

export default FactoringClientSelector;
